import { useEffect, useLayoutEffect, useRef, useSyncExternalStore, type RefObject } from 'react';

/**
 * Story 24.7b — the bottom chrome store. Below 1024px the phone toast floats
 * (it is portalled to `body`, Story 24.3), so it needs to know how tall the
 * in-flow chrome under it is: the tab bar, or the chat composer inside a
 * thread. Each registers here; a ResizeObserver sums the `offsetHeight` of
 * every ACTIVE registered element into `--bottom-chrome` (px) on `<html>`,
 * which the toast region's `bottom` reads.
 *
 * It also answers "is a composer registered?" (`useComposerRegistered`), so
 * MainLayout's bottom slot renders nothing while the composer's own slot
 * holds the toast's reservation — one reservation, never two.
 *
 * `window` / `document` are touched only inside functions.
 */

export type BottomChromeKind = 'tabbar' | 'composer';

const BOTTOM_CHROME_PROPERTY = '--bottom-chrome';

const registered = new Map<HTMLElement, BottomChromeKind>();
const subscribers = new Set<() => void>();
let observer: ResizeObserver | null = null;
let composerRegistered = false;

function write(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (registered.size === 0) {
    root.style.removeProperty(BOTTOM_CHROME_PROPERTY);
    return;
  }
  let sum = 0;
  registered.forEach((_kind, el) => {
    sum += el.offsetHeight;
  });
  root.style.setProperty(BOTTOM_CHROME_PROPERTY, `${sum}px`);
}

function syncComposer(): void {
  const next = Array.from(registered.values()).includes('composer');
  if (next !== composerRegistered) {
    composerRegistered = next;
    subscribers.forEach((listener) => listener());
  }
}

function getObserver(): ResizeObserver | null {
  if (observer) return observer;
  if (typeof ResizeObserver === 'undefined') return null;
  observer = new ResizeObserver(() => write());
  return observer;
}

function register(el: HTMLElement, kind: BottomChromeKind): () => void {
  registered.set(el, kind);
  getObserver()?.observe(el);
  write();
  syncComposer();
  return () => {
    registered.delete(el);
    observer?.unobserve(el);
    write();
    syncComposer();
  };
}

/**
 * Register `ref`'s element as bottom chrome of `kind` while `active`. It is
 * removed on unmount or when it goes inactive.
 *
 * The element is re-checked after every render (not only when `active`
 * changes): a host such as ChatThreadSurface returns a skeleton first and
 * mounts the composer wrapper later, under the same ref.
 */
export function useBottomChrome(
  ref: RefObject<HTMLElement | null>,
  kind: BottomChromeKind,
  active: boolean,
): void {
  const current = useRef<{ el: HTMLElement; kind: BottomChromeKind; release: () => void } | null>(null);

  useLayoutEffect(() => {
    const el = active ? ref.current : null;
    const held = current.current;
    if (held && held.el === el && held.kind === kind) return;
    held?.release();
    current.current = el ? { el, kind, release: register(el, kind) } : null;
  });

  useEffect(
    () => () => {
      current.current?.release();
      current.current = null;
    },
    [],
  );
}

function subscribe(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

function getSnapshot(): boolean {
  return composerRegistered;
}

function getServerSnapshot(): boolean {
  return false;
}

/** True while a composer is registered (a phone chat thread is open). */
export function useComposerRegistered(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Test seam: forget every registration. */
export function __resetBottomChromeForTests(): void {
  observer?.disconnect();
  observer = null;
  registered.clear();
  write();
  syncComposer();
}
